import React from 'react';
import { Drawer, Box, Typography, IconButton, Button, CircularProgress, Divider, Autocomplete, TextField, Alert, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Euro as EuroIcon,
  Science as ScienceIcon,
  Edit as EditIcon,
  AutoAwesome as SparkleIcon,
  AccessTime as AccessTimeIcon,
  WarningAmber as WarningAmberIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { colors } from '../../../themes/typography';
import { getExperimentStatusColor } from '../../../themes/experimentColors';
import useSlotMonthlySales from '../hooks/useSlotMonthlySales';
import useSlotUnitEconomics from '../hooks/useSlotUnitEconomics';
import SlotUnitEconomics from './SlotUnitEconomics';
import RecentSalesSection from '../../../components/shared/RecentSalesSection';
import BackToAlertsLink from './BackToAlertsLink';
import useExperimentPerformance from '../hooks/useExperimentPerformance';
import MeasurementStatus from '../../../components/shared/MeasurementStatus';
import DateEditPopover from '../../../components/shared/DateEditPopover';
import StatusDropdown from '../../../components/shared/StatusDropdown';
import AppliedPricingDialog from '../../../components/shared/AppliedPricingDialog';
import RejectReasonDialog from '../../../components/RejectReasonDialog/RejectReasonDialog';
import { useRejectionModal } from '../../../hooks/useRejectionModal';
import { updateExperimentStatus, fetchCandidateSuggestion } from '../../ExperimentManagement/services/experimentManagementApi';
import { inlineUpdateExperiment } from '../../../data/problemsApi';
import { API_BASE_URL, createAuthHeader } from '../../../config/api.config';
import { getApiErrorMessage } from '../../../utils/apiError';
import axios from 'axios';
import {
  MachineQuadrant, AlternativeMeta, alternativeOptions, alternativeGroupLabel, useReplacementAlternatives
} from '../../../components/shared/ReplacementAlternatives';

// "Más margen" (first = recommendation) then "Más volumen", in this Autocomplete's option shape
const toAiOptions = (alternatives) => alternativeOptions(alternatives)
  .map(o => ({ ...o, id: o.productId, name: o.productName, isAi: true }));

const positive = (value) => (Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null);

/**
 * Resumen de "qué va a pasar" en el carril para un experimento ya aceptado o en marcha.
 * `appliedPricing` (los precios que el gateway agendó contra el ERP al aceptar) manda sobre
 * cualquier derivación local: es literalmente lo que va a quedar en la máquina.
 */
const buildOutcomeSummary = (exp, t) => {
  if (!exp || !['accepted', 'running'].includes(exp.status)) return null;

  const applied = exp.appliedPricing || null;
  const tenders = ['cash', 'card', 'bank', 'mobile']
    .map((key) => ({ key, value: positive(applied?.[key]) }))
    .filter((tender) => tender.value !== null);
  const appliedPrice = tenders.length > 0 ? tenders[0].value : null;
  const tendersDiffer = new Set(tenders.map((tender) => tender.value)).size > 1;
  const scheduledFor = applied?.scheduledFor || null;
  const basePrice = positive(exp.basePrice) ?? positive(exp.originalPrice);
  const round = (value) => Math.round(value * 100) / 100;

  if (exp.experimentType === 'alza_precio_sugerido') {
    const pct = Number(exp.markup);
    const hasPct = Number.isFinite(pct) && pct > 0;
    return {
      label: t('priceIncrease'), symbol: '↑', color: '#2e7d32', bg: '#EDF7ED', border: '#C8E6C9',
      from: basePrice,
      to: appliedPrice ?? positive(exp.targetPrice) ?? (basePrice && hasPct ? round(basePrice * (1 + pct / 100)) : null),
      pct: hasPct ? `+${pct}%` : null,
      tenders, tendersDiffer, scheduledFor,
    };
  }

  if (exp.experimentType === 'promo_precio_unitario') {
    const pct = Number(exp.candidateDiscounts?.[0] ?? exp.discount);
    const hasPct = Number.isFinite(pct) && pct > 0;
    return {
      label: t('discount'), symbol: '↓', color: '#1565c0', bg: '#E8F1FC', border: '#BBDEFB',
      from: basePrice,
      to: appliedPrice ?? (basePrice && hasPct ? round(basePrice * (1 - pct / 100)) : null),
      pct: hasPct ? `-${pct}%` : null,
      tenders, tendersDiffer, scheduledFor,
    };
  }

  if (exp.experimentType === 'ampliar_carril') {
    return {
      label: t('laneExpansion'), symbol: '➕', color: '#3730a3', bg: '#eef2ff', border: '#c7d2fe',
      productFrom: exp.originalProduct?.name || null,
      productTo: exp.candidateNames?.[0] || null,
      to: appliedPrice ?? positive(exp.comboPrices?.[0]?.candidatePrice),
      tenders, tendersDiffer, scheduledFor,
    };
  }

  if (exp.experimentType === 'reemplazo_producto' || exp.experimentType === 'promo_combo_combinado') {
    return {
      label: t('replacement'), symbol: '⇄', color: '#4B5563', bg: '#F3F4F6', border: '#E5E7EB',
      productFrom: exp.originalProduct?.name || null,
      productTo: exp.candidateNames?.[0] || null,
      to: appliedPrice ?? positive(exp.comboPrices?.[0]?.candidatePrice),
      tenders, tendersDiffer, scheduledFor,
    };
  }

  return null;
};

// Lo que cabe rotulado en el cajón: 12 meses, 8 trimestres o todos los años
const SALES_GRAIN_WINDOW = { month: 12, quarter: 8, year: Infinity };

/**
 * Serie del gráfico de ventas a partir de los totales mensuales. Empieza en el primer periodo con
 * ventas (sin ceros de antes de que el producto llegase a la máquina) y acaba en el periodo en
 * curso, marcado como parcial; los huecos intermedios son ceros.
 */
const buildSalesSeries = (months, grain, lang, t) => {
  if (!months?.length) return [];
  const periodOf = (year, month) => {
    if (grain === 'month') return year * 12 + month - 1;
    if (grain === 'quarter') return year * 4 + Math.floor((month - 1) / 3);
    return year;
  };

  const units = new Map();
  for (const row of months) {
    const [year, month] = row.month.split('-').map(Number);
    const period = periodOf(year, month);
    units.set(period, (units.get(period) || 0) + Number(row.units || 0));
  }

  const now = new Date();
  const current = periodOf(now.getFullYear(), now.getMonth() + 1);
  const start = Math.max(Math.min(...units.keys()), current - SALES_GRAIN_WINDOW[grain] + 1);
  const shortMonth = new Intl.DateTimeFormat(lang, { month: 'short' });
  const longMonth = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' });

  const series = [];
  for (let period = start; period <= current; period++) {
    let top = String(period);
    let bottom = '';
    let label = top;
    if (grain === 'month') {
      const date = new Date(Math.floor(period / 12), period % 12, 1);
      top = String(date.getMonth() + 1);
      bottom = shortMonth.format(date).replace('.', '');
      label = longMonth.format(date);
    } else if (grain === 'quarter') {
      top = t('machines_quarterShort', { q: (period % 4) + 1 });
      bottom = String(Math.floor(period / 4));
      label = `${top} ${bottom}`;
    }
    series.push({ period, top, bottom, label, units: units.get(period) || 0, partial: period === current });
  }
  return series;
};

// Rótulo del eje X en dos líneas: número del mes (o trimestre/año) y, debajo, su nombre (o año)
const SalesPeriodTick = ({ x, y, payload, series }) => {
  const point = series.find(p => p.period === payload.value);
  if (!point) return null;
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={10} textAnchor="middle" fontSize={10} fontWeight={600} fill={colors.text.secondary}>{point.top}</text>
      {point.bottom && (
        <text dy={22} textAnchor="middle" fontSize={9} fill={colors.text.muted}>{point.bottom}</text>
      )}
    </g>
  );
};

const SlotDetailDrawer = ({ open, onClose, slot, machineData, clientId, onJumpToSlot, onBack, period = 30 }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const storeId = machineData?.machineId;
  const productId = slot?.d_product_id;

  // Experiment performance data
  const experimentStatus = slot?.experiment?.status;
  const showPerformance = open && slot?.experiment && ['running', 'finished'].includes(experimentStatus);
  const { data: perfData, loading: perfLoading } = useExperimentPerformance(
    showPerformance ? slot.experiment.id : null,
    showPerformance ? clientId : null
  );

  const { data: salesData, loading: salesLoading } = useSlotMonthlySales(
    open ? storeId : null,
    open ? productId : null,
    open ? clientId : null
  );
  const [salesGrain, setSalesGrain] = React.useState('month');

  const { data: unitEconomics } = useSlotUnitEconomics(
    open ? storeId : null,
    open ? productId : null,
    open ? clientId : null,
    slot?.row,
    slot?.column
  );

  // Quick-edit state for experiment card
  const [dateAnchorEl, setDateAnchorEl] = React.useState(null);
  const [dateSaving, setDateSaving] = React.useState(false);
  const [localDateFrom, setLocalDateFrom] = React.useState(null);
  const [localDateTo, setLocalDateTo] = React.useState(null);
  const [discountValue, setDiscountValue] = React.useState(null);
  const [discountSaving, setDiscountSaving] = React.useState(false);
  const [statusChanging, setStatusChanging] = React.useState(false);
  const discountTimerRef = React.useRef(null);

  // Candidate replacement inline edit state
  const [selectedCandidate, setSelectedCandidate] = React.useState(null);
  const [candidateIsAi, setCandidateIsAi] = React.useState(false);
  const [aiCandidates, setAiCandidates] = React.useState([]);
  const [liveQuadrant, setLiveQuadrant] = React.useState(null);
  const [aiSuggestLoading, setAiSuggestLoading] = React.useState(false);
  const [allProducts, setAllProducts] = React.useState([]);
  const [allProductsLoading, setAllProductsLoading] = React.useState(false);
  const [candidateSaving, setCandidateSaving] = React.useState(false);
  // { pricing, apply } — tenders a confirmar antes de reprogramar un experimento ya agendado
  const [priceReview, setPriceReview] = React.useState(null);
  // Motivo por el que el ERP rechazó el último guardado (fecha demasiado próxima, etc.)
  const [actionError, setActionError] = React.useState(null);
  const allProductsCache = React.useRef(null);

  // Initialize discount and local dates when experiment changes
  React.useEffect(() => {
    if (slot?.experiment) {
      setDiscountValue(slot.experiment.candidateDiscounts?.[0] || slot.experiment.discount || 15);
      setLocalDateFrom(slot.experiment.dateFrom);
      setLocalDateTo(slot.experiment.dateTo);
    }
  }, [slot?.experiment?.id, slot?.experiment?.dateFrom, slot?.experiment?.dateTo]);

  const outcome = React.useMemo(() => buildOutcomeSummary(slot?.experiment, t), [slot?.experiment, t]);

  React.useEffect(() => { setActionError(null); }, [slot?.experiment?.id, open]);

  // Initialize candidate state + fetch all products when opening a replacement/combo/lane-expansion experiment
  const isReplacement = slot?.experiment?.experimentType === 'reemplazo_producto'
    || slot?.experiment?.experimentType === 'promo_combo_combinado'
    || slot?.experiment?.experimentType === 'ampliar_carril';
  const currentCandidateId = slot?.experiment?.candidateProductIds?.[0];
  const currentCandidateName = slot?.experiment?.candidateNames?.[0];

  React.useEffect(() => {
    if (open && slot?.experiment && isReplacement) {
      // Sync selected candidate from live experiment data
      if (currentCandidateId) {
        setSelectedCandidate(prev => {
          // Only update if the backend data actually changed
          if (prev?.id === currentCandidateId) return prev;
          return { id: currentCandidateId, name: currentCandidateName || '?' };
        });
        setCandidateIsAi(false);
      } else {
        setSelectedCandidate(null);
      }

      // Fetch all products for manual search
      if (clientId && !allProductsCache.current) {
        setAllProductsLoading(true);
        const token = localStorage.getItem('access_token');
        axios.get(`${API_BASE_URL}/all-products?clientId=${clientId}`, { headers: createAuthHeader(token) })
          .then(res => {
            const products = res.data.products || [];
            allProductsCache.current = products;
            setAllProducts(products);
          })
          .finally(() => setAllProductsLoading(false));
      } else if (allProductsCache.current) {
        setAllProducts(allProductsCache.current);
      }
    }
  }, [open, slot?.experiment?.id, currentCandidateId, currentCandidateName]);

  // Reset AI candidates when experiment changes — pre-populate for manual_review
  React.useEffect(() => {
    if (slot?.experiment?.status === 'manual_review' && slot.experiment.candidateProductIds?.length > 0) {
      // Pre-load the AI-recommended candidates from experiment data
      const ids = slot.experiment.candidateProductIds;
      const names = slot.experiment.candidateNames || [];
      setAiCandidates(ids.map((id, i) => ({
        id,
        name: names[i] || 'Unknown',
        isAi: true
      })));
      // Auto-select first candidate
      setSelectedCandidate({ id: ids[0], name: names[0] || '?' });
      setCandidateIsAi(true);
    } else {
      setAiCandidates([]);
    }
    setLiveQuadrant(null);
  }, [slot?.experiment?.id]);

  // AI suggest handler for replacement candidates
  const handleAiSuggest = async () => {
    if (!storeId || !productId || !clientId) return;
    setAiSuggestLoading(true);
    try {
      // ampliar_carril también pide sugerencias de reemplazo: el beneficiario se elige igual
      const result = await fetchCandidateSuggestion(clientId, storeId, productId, 'reemplazo_producto');
      if (result?.candidates?.length > 0) {
        // Margin/volume groups only describe a replacement; a combo keeps its single AI list.
        setAiCandidates(['reemplazo_producto', 'ampliar_carril'].includes(slot?.experiment?.experimentType)
          ? toAiOptions({ margin: result.candidates, volume: result.volumeCandidates || [] })
          : result.candidates.map(c => ({
            id: c.productId,
            name: c.productName,
            improvementPercentage: c.improvementPercentage,
            isAi: true
          })));
        setLiveQuadrant(result.quadrant || null);
        // Auto-select top candidate
        const top = result.candidates[0];
        setSelectedCandidate({ id: top.productId, name: top.productName, improvementPercentage: top.improvementPercentage });
        setCandidateIsAi(true);
      }
    } finally { setAiSuggestLoading(false); }
  };

  const isEditable = ['pending', 'accepted', 'manual_review'].includes(slot?.experiment?.status);
  // Una propuesta pendiente la reescribe la corrida de cada noche: el producto elegido sólo se guarda
  // al aceptarla, en la misma petición, igual que en Problemas.
  const deferCandidateToAccept = slot?.experiment?.status === 'manual_review'
    && ['reemplazo_producto', 'ampliar_carril'].includes(slot?.experiment?.experimentType);

  // Saved alternatives of an editable replacement: used until the sparkle brings a live suggestion
  const alternativesEnabled = open && isEditable
    && ['reemplazo_producto', 'ampliar_carril'].includes(slot?.experiment?.experimentType);
  const savedAlternatives = useReplacementAlternatives(clientId, slot?.experiment?.id, alternativesEnabled);
  const saved = alternativesEnabled ? savedAlternatives : null;
  const hasLiveAlternatives = aiCandidates.some(o => o.group);
  const aiOptions = !hasLiveAlternatives && (saved?.margin?.length || saved?.volume?.length)
    ? toAiOptions(saved)
    : aiCandidates;
  const machineQuadrant = hasLiveAlternatives ? liveQuadrant : saved?.quadrant;

  // Ya agendado contra el ERP: cualquier cambio de producto o de % reprograma precios, así que
  // el operador confirma antes los nuevos en el mismo diálogo de tenders que al aceptar.
  const needsPriceConfirmation = ['accepted', 'running'].includes(slot?.experiment?.status);

  // Pide el precio que quedaría tras el cambio y abre el diálogo; `apply` reintenta el guardado
  // con los tenders confirmados. Devuelve false si no hay precio que confirmar (se guarda ya).
  const requestPriceConfirmation = async (payload, apply) => {
    const token = localStorage.getItem('access_token');
    const preview = await axios.post(
      `${API_BASE_URL}/experiments/pricing-preview?clientId=${clientId}`,
      { experimentId: slot.experiment.id, ...payload },
      { headers: createAuthHeader(token) }
    );
    if (!preview.data?.pricing) return false;
    setPriceReview({ pricing: preview.data.pricing, apply });
    return true;
  };

  // Date save handler
  const handleDateSave = async ({ beginDate, endDate }) => {
    setDateSaving(true);
    setActionError(null);
    const rollbackFrom = localDateFrom;
    const rollbackTo = localDateTo;
    setLocalDateFrom(beginDate);
    setLocalDateTo(endDate);
    setDateAnchorEl(null);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/experiments/${slot.experiment.id}/dates?clientId=${clientId}`,
        { dateFrom: beginDate, dateTo: endDate },
        { headers: createAuthHeader(token) }
      );
      window.dispatchEvent(new CustomEvent('experimentMutation', { detail: { action: 'updateDates' } }));
    } catch (error) {
      // El ERP puede rechazar la fecha (margen mínimo): no se guardó nada, así que se deshace
      // el cambio optimista y se enseña el motivo.
      setLocalDateFrom(rollbackFrom);
      setLocalDateTo(rollbackTo);
      setActionError(getApiErrorMessage(error, t('actionFailed', { defaultValue: 'No se pudo guardar' })));
    } finally { setDateSaving(false); }
  };

  // Status change handler; rejecting asks for the reason first (the dialog calls back with it)
  const handleStatusChange = async (newStatus, pricingOverride = null, reason = '') => {
    if (newStatus === 'rejected' && !reason) {
      rejectModal.openRejectDialog(slot.experiment.id);
      return;
    }
    setStatusChanging(true);
    try {
      // Aceptar agenda los precios contra el ERP: se confirman antes, igual que en el kanban
      // y en Problemas. Aquí NO se ignora el p1 del orquestador: la recomendación sigue siendo
      // válida mientras no se toque el producto ni el %.
      const pickedId = deferCandidateToAccept && newStatus === 'accepted'
        && selectedCandidate?.id && selectedCandidate.id !== currentCandidateId ? selectedCandidate.id : null;
      if (newStatus === 'accepted' && !pricingOverride) {
        const opened = await requestPriceConfirmation(pickedId ? { candidateProductId: pickedId } : {}, (override) => handleStatusChange(newStatus, override));
        if (opened) return;
      }
      if (pickedId) {
        await inlineUpdateExperiment(clientId, slot.experiment.id, {
          status: 'accepted', candidateProductId: pickedId, ...(pricingOverride ? { pricingOverride } : {}),
        });
        window.dispatchEvent(new CustomEvent('experimentMutation', { detail: { action: 'statusChange' } }));
        return;
      }
      // `updateExperimentStatus` no lanza: devuelve {success:false, message} con el motivo ya
      // traducido, así que hay que mirarlo o el rechazo del ERP pasa desapercibido.
      const result = await updateExperimentStatus(slot.experiment.id, newStatus, clientId, reason, pricingOverride);
      if (result && result.success === false) {
        setActionError(result.message || t('actionFailed', { defaultValue: 'No se pudo guardar' }));
        return;
      }
      window.dispatchEvent(new CustomEvent('experimentMutation', { detail: { action: 'statusChange' } }));
    } catch (e) {
      console.error('Status change failed:', e);
      setActionError(getApiErrorMessage(e, t('actionFailed', { defaultValue: 'No se pudo guardar' })));
    }
    finally { setStatusChanging(false); }
  };
  const rejectModal = useRejectionModal((_experimentId, reason) => handleStatusChange('rejected', null, reason));

  // Discount save handler
  const handleDiscountSave = async (pricingOverride = null) => {
    setDiscountSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const exp = slot.experiment;
      if (needsPriceConfirmation && !pricingOverride) {
        const opened = await requestPriceConfirmation(
          { discount: discountValue, ignoreOrchestratorTarget: true },
          (override) => handleDiscountSave(override)
        );
        if (opened) return;
      }
      await axios.post(
        `${API_BASE_URL}/experiments/${exp.id}/candidate?clientId=${clientId}`,
        {
          candidateProductId: exp.candidateProductIds?.[0] || exp.originalProduct?.id,
          discount: discountValue,
          clientId,
          experimentType: exp.experimentType,
          ...(pricingOverride ? { pricingOverride } : {})
        },
        { headers: createAuthHeader(token) }
      );
      window.dispatchEvent(new CustomEvent('experimentMutation', { detail: { action: 'updateDiscount' } }));
    } catch (error) {
      setActionError(getApiErrorMessage(error, t('actionFailed', { defaultValue: 'No se pudo guardar' })));
    } finally { setDiscountSaving(false); }
  };

  // Candidate save handler (replacement/combo)
  const handleCandidateSave = async (candidate, isAi, pricingOverride = null) => {
    const c = candidate || selectedCandidate;
    const ai = isAi !== undefined ? isAi : candidateIsAi;
    if (!c || !slot?.experiment) return;
    setCandidateSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      const exp = slot.experiment;
      const discountForCandidate = exp.candidateDiscounts?.[0] || 0;
      if (needsPriceConfirmation && !pricingOverride) {
        const opened = await requestPriceConfirmation(
          { newDProductId: c.id, discount: discountForCandidate, ignoreOrchestratorTarget: true },
          (override) => handleCandidateSave(c, ai, override)
        );
        if (opened) return;
      }
      await axios.post(
        `${API_BASE_URL}/experiments/${exp.id}/candidate?clientId=${clientId}`,
        {
          candidateProductId: c.id,
          discount: discountForCandidate,
          clientId,
          experimentType: exp.experimentType,
          isAi: ai,
          ...(pricingOverride ? { pricingOverride } : {})
        },
        { headers: createAuthHeader(token) }
      );
      window.dispatchEvent(new CustomEvent('experimentMutation', { detail: { action: 'updateCandidate' } }));
    } catch (error) {
      setActionError(getApiErrorMessage(error, t('actionFailed', { defaultValue: 'No se pudo guardar' })));
    } finally { setCandidateSaving(false); }
  };

  // Sales chart series (must be before any early return); switching grain regroups locally, no request
  const salesSeries = React.useMemo(
    () => buildSalesSeries(salesData?.months, salesGrain, i18n.language || 'es', t),
    [salesData, salesGrain, i18n.language, t]
  );

  if (!slot || !slot.name) return null;

  const hasExperiment = Boolean(slot.experiment) && slot.experiment.status !== 'rejected';

  // Verdict helper: human-readable trend
  const getVerdict = (delta) => {
    if (delta === null || delta === undefined) return null;
    if (delta > 5) return { label: `▲ ${t('machines_growing')}`, color: colors.status.success };
    if (delta < -15) return { label: `▼ ${t('machines_declining')}`, color: colors.critical?.text || '#ef4444' };
    return { label: `— ${t('machines_stable')}`, color: colors.text.secondary };
  };

  // Previous period daily average
  const prevAvgSales = slot.previousPeriodSales > 0 ? (slot.previousPeriodSales / period).toFixed(1) : null;

  // Previous period margin per unit
  const prevMarginPerUnit = slot.previousPeriodMargin > 0
    ? Number(slot.previousPeriodMargin).toFixed(2)
    : null;

  // Status pill renderer (matching EnhancedSlot style)
  const renderStatusPill = () => {
    if (!hasExperiment) return null;
    const status = slot.experiment.status;
    const statusColor = getExperimentStatusColor(status);
    const isManualReview = status === 'manual_review';
    const statusLabel = isManualReview ? t('machines_aiSuggestion') : t(status);
    const StatusIcon = isManualReview ? SparkleIcon : ScienceIcon;
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '3px',
          fontSize: '12px',
          fontWeight: 700,
          color: statusColor,
          backgroundColor: `${statusColor}18`,
          border: `1px solid ${statusColor}30`,
          px: '6px',
          py: '2px',
          borderRadius: '4px',
          lineHeight: 1.2,
        }}
      >
        <StatusIcon sx={{ fontSize: 14, color: statusColor }} />
        {statusLabel}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 460 },
          maxWidth: '100vw',
          p: 0
        }
      }}
    >
      {/* Precios que se van a reprogramar en el ERP tras el cambio (mismo diálogo que aceptar) */}
      <AppliedPricingDialog
        open={Boolean(priceReview)}
        pricing={priceReview?.pricing}
        onClose={() => setPriceReview(null)}
        onConfirm={(pricingOverride) => {
          const review = priceReview;
          setPriceReview(null);
          review?.apply(pricingOverride);
        }}
      />
      <RejectReasonDialog {...rejectModal.dialogProps} />

      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${colors.border.light}`,
        backgroundColor: colors.background.surface
      }}>
        <Box>
          {onBack && <BackToAlertsLink onClick={onBack} sx={{ mb: 0.5 }} />}
          <Typography sx={{ fontSize: '15px', fontWeight: 600, color: colors.text.primary }}>
            {slot.name}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: colors.text.muted }}>
            {slot.selection_id || `${slot.row}-${slot.column}`} • {machineData?.location}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {/* KPIs - Absolute values + Verdict */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {/* Avg Sales */}
            <Box sx={{
              p: 1.5,
              borderRadius: '8px',
              border: `1px solid ${colors.border.light}`,
              backgroundColor: colors.background.subtle
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <TrendingUpIcon sx={{ fontSize: 14, color: colors.text.muted }} />
                <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>
                  {t('machines_tooltipAvgSales')}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, color: colors.text.primary }}>
                {slot.productAvgSales > 0 ? `${Number(slot.productAvgSales).toFixed(1)} u/d` : '–'}
              </Typography>
              {prevAvgSales && (
                <Typography sx={{ fontSize: '11px', color: colors.text.muted, mt: 0.25 }}>
                  {t('machines_prevPeriod', { value: `${prevAvgSales}u/d` })}
                </Typography>
              )}
              {getVerdict(slot.salesDeltaPercentage) && (
                <Typography sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: getVerdict(slot.salesDeltaPercentage).color,
                  mt: 0.25
                }}>
                  {getVerdict(slot.salesDeltaPercentage).label}
                </Typography>
              )}
            </Box>

            {/* Margin */}
            <Box sx={{
              p: 1.5,
              borderRadius: '8px',
              border: `1px solid ${colors.border.light}`,
              backgroundColor: colors.background.subtle
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <EuroIcon sx={{ fontSize: 14, color: colors.text.muted }} />
                <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>
                  {t('machines_tooltipMargin')}
                </Typography>
              </Box>
              {unitEconomics?.price != null ? (
                <SlotUnitEconomics data={unitEconomics} />
              ) : (
                <>
                  <Typography sx={{ fontSize: '18px', fontWeight: 600, color: colors.text.primary }}>
                    {slot.productMargin > 0 ? `€${Number(slot.productMargin).toFixed(2)}/u` : '–'}
                  </Typography>
                  {prevMarginPerUnit && (
                    <Typography sx={{ fontSize: '11px', color: colors.text.muted, mt: 0.25 }}>
                      {t('machines_prevPeriod', { value: `€${prevMarginPerUnit}/u` })}
                    </Typography>
                  )}
                  {getVerdict(slot.marginDeltaPercentage) && (
                    <Typography sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: getVerdict(slot.marginDeltaPercentage).color,
                      mt: 0.25
                    }}>
                      {getVerdict(slot.marginDeltaPercentage).label}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Experiment card */}
        {hasExperiment && slot?.experiments?.length > 1 && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary, mb: 1 }}>
              {slot.experiments.length} {t('machines_splitExperiments', 'experiments on this slot')}
            </Typography>
            {slot.experiments.map((exp) => (
              <Box key={exp.id} sx={{
                p: 1.5, mb: 1, borderRadius: '8px',
                border: `2px solid ${getExperimentStatusColor(exp.status)}`,
                backgroundColor: colors.background.subtle
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: '2px',
                    fontSize: '10px', fontWeight: 700, color: getExperimentStatusColor(exp.status),
                    backgroundColor: `${getExperimentStatusColor(exp.status)}18`,
                    border: `1px solid ${getExperimentStatusColor(exp.status)}30`,
                    px: '4px', py: '1px', borderRadius: '3px'
                  }}>
                    {t(exp.status)}
                  </Box>
                  <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>#{exp.id}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: colors.text.primary }}>
                    {exp.originalProduct?.name || slot.name}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>→</Typography>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#0969da' }}>
                    {exp.newProductName || exp.candidateNames?.[0] || exp.description?.split('with ')?.[1] || `#${exp.newDProductId}`}
                  </Typography>
                </Box>
                {exp.dateFrom && (
                  <Typography sx={{ fontSize: '10px', color: colors.text.muted, mt: 0.25 }}>
                    {new Date(exp.dateFrom).toLocaleDateString()} – {new Date(exp.dateTo).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {hasExperiment && !(slot?.experiments?.length > 1) && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary, mb: 1 }}>
              {t('machines_tooltipExperiment')}
            </Typography>
            <Box sx={{
              p: 1.5,
              borderRadius: '8px',
              border: `2px solid ${getExperimentStatusColor(slot.experiment.status)}`,
              backgroundColor: colors.background.subtle
            }}>
              {/* Status + ID row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {slot.experiment.status === 'manual_review' ? (
                  renderStatusPill()
                ) : isEditable ? (
                  <StatusDropdown
                    currentStatus={slot.experiment.status}
                    experimentId={slot.experiment.id}
                    onStatusChange={handleStatusChange}
                    size="compact"
                  />
                ) : renderStatusPill()}
                <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>
                  #{slot.experiment.id}
                </Typography>
              </Box>

              {actionError && (
                <Alert severity="error" sx={{ mb: 0.75, fontSize: '11px', py: 0, '& .MuiAlert-message': { py: 0.75 } }}>
                  {actionError}
                </Alert>
              )}

              {/* Qué va a pasar en el carril: dirección, precio final y (si difieren) cada tender */}
              {outcome && (
                <Box sx={{
                  p: 1,
                  mb: 0.75,
                  borderRadius: '6px',
                  backgroundColor: outcome.bg,
                  border: `1px solid ${outcome.border}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: outcome.color, lineHeight: 1.2 }}>
                      {outcome.symbol} {outcome.label}
                    </Typography>
                    {outcome.pct && (
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: outcome.color, lineHeight: 1.2 }}>
                        {outcome.pct}
                      </Typography>
                    )}
                  </Box>

                  {(outcome.productFrom || outcome.productTo) && (
                    <Typography sx={{ fontSize: '11px', color: colors.text.primary, mt: 0.25, lineHeight: 1.3 }}>
                      <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600 }}>{outcome.productFrom || '?'}</Typography>
                      {' → '}
                      <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600 }}>{outcome.productTo || '–'}</Typography>
                    </Typography>
                  )}

                  {outcome.to !== null && outcome.to !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      {outcome.from ? (
                        <>
                          <Typography sx={{ fontSize: '12px', color: colors.text.muted, textDecoration: 'line-through' }}>
                            €{outcome.from.toFixed(2)}
                          </Typography>
                          <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>→</Typography>
                        </>
                      ) : (
                        <Typography sx={{ fontSize: '11px', color: colors.text.secondary }}>{t('price')}:</Typography>
                      )}
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: outcome.color }}>
                        €{Number(outcome.to).toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  {outcome.tendersDiffer && (
                    <Typography sx={{ fontSize: '10px', color: colors.text.secondary, mt: 0.25, lineHeight: 1.3 }}>
                      {outcome.tenders.map((tender) => `${t(`appliedPrice${tender.key.charAt(0).toUpperCase()}${tender.key.slice(1)}`)} €${tender.value.toFixed(2)}`).join(' · ')}
                    </Typography>
                  )}

                  {outcome.scheduledFor && (
                    <Typography sx={{ fontSize: '10px', color: colors.text.muted, mt: 0.25 }}>
                      {t('scheduledFor')} {new Date(outcome.scheduledFor).toLocaleDateString('sv')}
                    </Typography>
                  )}
                </Box>
              )}

              {/* AI Recommendation context box */}
              {slot.experiment.status === 'manual_review' && (slot.experiment.aiRecommendation || slot.experiment.slotGuidance) && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.75,
                  p: 1,
                  mb: 0.75,
                  borderRadius: '6px',
                  background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
                  border: '1px solid #C4B5FD',
                }}>
                  <SparkleIcon sx={{ fontSize: 16, color: '#7C3AED', flexShrink: 0, mt: '1px' }} />
                  <Box>
                    {slot.experiment.aiRecommendation && (
                      <Typography sx={{ fontSize: '11px', color: '#5B21B6', lineHeight: 1.4, fontWeight: 500 }}>
                        {slot.experiment.aiRecommendation}
                      </Typography>
                    )}
                    {slot.experiment.slotGuidance && (
                      <Typography sx={{ fontSize: '10px', color: '#6D28D9', lineHeight: 1.4, mt: 0.25, fontStyle: 'italic' }}>
                        {slot.experiment.slotGuidance}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {/* Swap warning banner for replacement/lane-expansion experiments where planogram still shows original */}
              {['reemplazo_producto', 'ampliar_carril'].includes(slot.experiment.experimentType)
                && slot.experiment.status === 'running'
                && slot.experiment.isSwapped === false && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.75,
                  p: 1,
                  mb: 0.75,
                  borderRadius: '6px',
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #FDE68A',
                }}>
                  <WarningAmberIcon sx={{ fontSize: 16, color: '#92400E', flexShrink: 0, mt: '1px' }} />
                  <Typography sx={{ fontSize: '11px', color: '#92400E', lineHeight: 1.4 }}>
                    {t('machines_swapWarning', {
                      original: slot.experiment.originalProduct?.name || '?',
                      replacement: slot.experiment.candidateNames?.[0] || '?'
                    })}
                  </Typography>
                </Box>
              )}

              {/* Type-specific detail */}
              {slot.experiment.experimentType === 'promo_precio_unitario' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Typography sx={{ fontSize: '12px', color: colors.text.secondary }}>
                    {t('discount')}:
                  </Typography>
                  {isEditable ? (
                    <>
                      <input
                        type="number"
                        value={discountValue ?? ''}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setDiscountValue(val);
                          clearTimeout(discountTimerRef.current);
                          discountTimerRef.current = setTimeout(() => {
                            if (val >= 1 && val <= 99) handleDiscountSave();
                          }, 800);
                        }}
                        style={{
                          width: 48,
                          fontSize: '12px',
                          padding: '2px 4px',
                          border: `1px solid ${colors.border.light}`,
                          borderRadius: 4,
                          textAlign: 'center'
                        }}
                        min={1}
                        max={99}
                      />
                      <Typography sx={{ fontSize: '12px', color: colors.text.secondary }}>%</Typography>
                      {discountSaving && <CircularProgress size={14} />}
                    </>
                  ) : (
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: colors.text.primary }}>
                      -{slot.experiment.candidateDiscounts?.[0] || slot.experiment.discount || 15}%
                    </Typography>
                  )}
                </Box>
              )}
              {isReplacement && !isEditable && (
                <Typography sx={{ fontSize: '11px', color: colors.text.secondary, mt: 0.5 }}>
                  <Typography component="span" sx={{ fontSize: '11px', fontWeight: 600, color: colors.text.primary }}>
                    {slot.experiment.originalProduct?.name || '?'}
                  </Typography>
                  {' → '}
                  {slot.experiment.candidateNames?.[0] || '–'}
                </Typography>
              )}
              {isReplacement && isEditable && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', mt: 0.5, flexWrap: 'nowrap' }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: colors.text.primary, flexShrink: 0, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1 }} title={slot.experiment.originalProduct?.name}>
                    {slot.experiment.originalProduct?.name || '?'}
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: colors.text.muted, flexShrink: 0, lineHeight: 1 }}>→</Typography>
                    <Autocomplete
                      size="small"
                      disableClearable
                      forcePopupIcon={false}
                      value={selectedCandidate}
                      options={aiOptions.length > 0
                        ? [...aiOptions, ...allProducts.filter(p => !aiOptions.some(ai => ai.id === p.id))]
                        : allProducts
                      }
                      groupBy={(option) => option.group
                        ? alternativeGroupLabel(t, option.group)
                        : option.isAi ? t('machines_aiCandidates') : t('machines_allProducts')}
                      getOptionLabel={(opt) => opt?.name || ''}
                      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                      loading={allProductsLoading}
                      filterOptions={(options, state) => {
                        if (!state.inputValue || state.inputValue.length < 2) {
                          return options.filter(o => o.isAi);
                        }
                        const filter = state.inputValue.toLowerCase();
                        return options
                          .filter(o => o.isAi || (o.name && o.name.toLowerCase().includes(filter)))
                          .slice(0, 25);
                      }}
                      noOptionsText={t('machines_typeToSearch')}
                      onChange={(_, newValue) => {
                        setSelectedCandidate(newValue);
                        const isAi = Boolean(newValue?.isAi);
                        setCandidateIsAi(isAi);
                        if (newValue && !deferCandidateToAccept) handleCandidateSave(newValue, isAi);
                      }}
                      PaperComponent={({ children, ...paperProps }) => (
                        <Box {...paperProps} sx={{ ...paperProps.sx, backgroundColor: 'white', borderRadius: '8px', border: `1px solid ${colors.border.light}`, boxShadow: `0 4px 16px ${colors.shadow.soft}` }}>
                          {children}
                          {aiOptions.length > 0 && (
                            <Typography sx={{ fontSize: '9px', fontStyle: 'italic', color: colors.text.muted, textAlign: 'center', py: 0.5, borderTop: `1px solid ${colors.border.light}` }}>
                              {t('machines_typeToSearchOther')}
                            </Typography>
                          )}
                        </Box>
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.key || option.id} style={{ padding: '2px 8px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                            {(option.group ? option.recommended : option.isAi) && <SparkleIcon sx={{ fontSize: 11, color: '#f59e0b' }} />}
                            <Typography sx={{ fontSize: '10px', flex: 1 }} noWrap>{option.name}</Typography>
                            {option.group ? <AlternativeMeta option={option} /> : option.improvementPercentage != null && (
                              <Typography sx={{
                                fontSize: '9px',
                                fontWeight: 600,
                                color: option.improvementPercentage >= 0 ? colors.status.success : '#ef4444'
                              }}>
                                {option.improvementPercentage >= 0 ? '+' : ''}{Math.round(option.improvementPercentage)}%
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={selectedCandidate ? '' : t('machines_searchProduct')}
                          size="small"
                        />
                      )}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: 'calc(100% - 110px)',
                        '& .MuiInputBase-root': { fontSize: '10px', py: 0, height: 24, minHeight: 24 },
                        '& .MuiInputBase-input': { py: '1px !important', px: '4px !important', textOverflow: 'ellipsis', overflow: 'hidden' },
                        '& .MuiAutocomplete-endAdornment': { right: '2px', top: 'calc(50% - 10px)' },
                        '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { fontSize: 14 },
                        '& .MuiAutocomplete-clearIndicator': { p: 0 },
                        '& .MuiAutocomplete-popupIndicator': { p: 0 },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleAiSuggest}
                      disabled={aiSuggestLoading}
                      title={t('machines_aiSuggest')}
                      sx={{ p: 0.25, flexShrink: 0, color: '#f59e0b' }}
                    >
                      {aiSuggestLoading ? <CircularProgress size={12} /> : <SparkleIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                    {candidateSaving && <CircularProgress size={12} sx={{ flexShrink: 0 }} />}
                </Box>
              )}
              {isReplacement && isEditable && (
                <MachineQuadrant quadrant={machineQuadrant} sx={{ display: 'block', mt: 0.5 }} />
              )}

              {/* Dates row with edit */}
              {(localDateFrom || slot.experiment.dateFrom) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Typography sx={{ fontSize: '11px', color: colors.text.muted }}>
                    {new Date(localDateFrom || slot.experiment.dateFrom).toLocaleDateString('sv')} → {new Date(localDateTo || slot.experiment.dateTo).toLocaleDateString('sv')}
                  </Typography>
                  {dateSaving && <CircularProgress size={12} />}
                  {isEditable && (
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setDateAnchorEl(e.currentTarget); }}
                      sx={{ p: 0.25 }}
                    >
                      <EditIcon sx={{ fontSize: 13, color: colors.text.muted }} />
                    </IconButton>
                  )}
                </Box>
              )}

              {/* Accept / Reject buttons for manual_review (below dates) */}
              {slot.experiment.status === 'manual_review' && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    disabled={statusChanging}
                    startIcon={statusChanging ? <CircularProgress size={14} color="inherit" /> : <CheckIcon />}
                    onClick={() => handleStatusChange('accepted')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '12px',
                      backgroundColor: '#10B981',
                      '&:hover': { backgroundColor: '#059669' }
                    }}
                  >
                    {t('accept')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={statusChanging}
                    startIcon={statusChanging ? <CircularProgress size={14} color="inherit" /> : <CloseIcon />}
                    onClick={() => handleStatusChange('rejected')}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '12px',
                      color: '#EF4444',
                      borderColor: '#EF4444',
                      '&:hover': { borderColor: '#DC2626', backgroundColor: '#EF44440A' }
                    }}
                  >
                    {t('reject')}
                  </Button>
                </Box>
              )}

              {/* Experiment elapsed progress bar (not shown for manual_review) */}
              {slot.experiment.status !== 'manual_review' && slot.experiment.dateFrom && slot.experiment.dateTo && (() => {
                const start = new Date(slot.experiment.dateFrom).getTime();
                const end = new Date(slot.experiment.dateTo).getTime();
                const now = Date.now();
                const total = end - start;
                const elapsed = Math.max(0, Math.min(now - start, total));
                const pct = total > 0 ? (elapsed / total) * 100 : 0;
                const statusColor = getExperimentStatusColor(slot.experiment.status);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                    <AccessTimeIcon sx={{ fontSize: 13, color: colors.text.muted, flexShrink: 0 }} />
                    <Box sx={{
                      flex: 1,
                      height: 5,
                      borderRadius: 3,
                      backgroundColor: `${statusColor}20`,
                      overflow: 'hidden',
                    }}>
                      <Box sx={{
                        width: `${pct}%`,
                        height: '100%',
                        borderRadius: 3,
                        backgroundColor: statusColor,
                        transition: 'width 300ms ease',
                      }} />
                    </Box>
                  </Box>
                );
              })()}

              {/* Inline performance metrics */}
              {showPerformance && (
                <>
                  {perfLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.75 }}>
                      <CircularProgress size={14} />
                    </Box>
                  )}
                  {/* Running: how the count goes, no delta until it ends */}
                  {!perfLoading && perfData?.measurement && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${colors.border.light}` }}>
                      <MeasurementStatus
                        measurement={perfData.measurement}
                        units={perfData.performance?.units}
                        baselineUnits={perfData.comparisons?.historical?.metadata?.hasData === false ? null : perfData.comparisons?.historical?.baseline?.units}
                        zeroBaseline={perfData.comparisons?.historical?.metadata?.zeroBaseline}
                        lanes={slot?.selection_id ? [slot.selection_id] : []}
                        machineId={String(machineData?.location || storeId)}
                      />
                    </Box>
                  )}
                  {!perfLoading && perfData?.comparisons?.historical?.delta?.units != null && (() => {
                    const delta = perfData.comparisons.historical.delta;
                    const baseline = perfData.comparisons.historical.baseline;
                    const perf = perfData.performance;
                    const metrics = [
                      { key: 'u', label: t('machines_deltaUnits'), delta: delta.units, before: baseline?.units, after: perf?.units, fmt: (v) => Math.round(v) },
                      { key: 'r', label: t('machines_deltaRevenue'), delta: delta.revenue, before: baseline?.revenue, after: perf?.revenue, fmt: (v) => `€${Math.round(v)}` },
                      { key: 'gp', label: t('machines_deltaGrossProfit'), delta: delta.grossProfit, before: baseline?.grossProfit, after: perf?.grossProfit, fmt: (v) => `€${Math.round(v)}` },
                    ];
                    return (
                      <Box sx={{ mt: 1, pt: 1, mx: -1, borderTop: `1px solid ${colors.border.light}` }}>
                        <Box sx={{ display: 'flex', gap: 0 }}>
                          {metrics.map((m, i) => {
                            const isPositive = m.delta >= 0;
                            const deltaColor = isPositive ? '#448361' : '#d9534f';
                            return (
                              <Box key={m.key} sx={{
                                flex: 1,
                                minWidth: 0,
                                textAlign: 'center',
                                ...(i < metrics.length - 1 && { borderRight: `1px solid ${colors.border.light}` }),
                              }}>
                                <Typography sx={{ fontSize: '9px', color: colors.text.muted, lineHeight: 1, mb: 0.25 }}>
                                  {m.label}
                                </Typography>
                                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: deltaColor, lineHeight: 1.2 }}>
                                  {isPositive ? '+' : ''}{m.fmt(m.delta)}
                                </Typography>
                                {m.before != null && m.after != null && (
                                  <Typography sx={{ fontSize: '8px', color: colors.text.muted, lineHeight: 1.3, mt: 0.25 }}>
                                    {m.fmt(m.before)} → {m.fmt(m.after)}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                        <Typography
                          onClick={() => navigate('/experiments-overview', {
                            state: { filters: { storeId: [String(machineData?.location || storeId)] } }
                          })}
                          sx={{
                            fontSize: '10px',
                            color: colors.action?.primary || '#2563eb',
                            mt: 0.75,
                            cursor: 'pointer',
                            textAlign: 'right',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {t('machines_viewInOverview')} →
                        </Typography>
                      </Box>
                    );
                  })()}
                  {!perfLoading && !perfData?.measurement && (!perfData?.comparisons?.historical?.delta) && (
                    <Typography sx={{ fontSize: '10px', color: colors.text.muted, textAlign: 'center', mt: 0.75, pt: 0.75, borderTop: `1px solid ${colors.border.light}` }}>
                      {t('machines_noPerformanceData')}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {/* Date edit popover */}
            <DateEditPopover
              open={Boolean(dateAnchorEl)}
              anchorEl={dateAnchorEl}
              onClose={() => setDateAnchorEl(null)}
              currentStartDate={localDateFrom || slot.experiment.dateFrom}
              currentEndDate={localDateTo || slot.experiment.dateTo}
              onSave={handleDateSave}
              loading={dateSaving}
              t={t}
            />
          </Box>
        )}

        {hasExperiment && <Divider />}

        {/* Sales per month / quarter / year */}
        <Box sx={{ px: 2, py: 1.5 }} data-testid="slot-sales-chart">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary }}>
              {t('machines_salesHistory')}
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={salesGrain}
              onChange={(e, value) => { if (value) setSalesGrain(value); }}
              aria-label={t('machines_salesGrain')}
            >
              <ToggleButton value="month" sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none' }}>
                {t('machines_salesGrainMonth')}
              </ToggleButton>
              <ToggleButton value="quarter" sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none' }}>
                {t('machines_salesGrainQuarter')}
              </ToggleButton>
              <ToggleButton value="year" sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none' }}>
                {t('machines_salesGrainYear')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {salesLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!salesLoading && salesSeries.length > 0 && (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={salesSeries} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border.light} vertical={false} />
                <XAxis
                  dataKey="period"
                  interval={0}
                  height={34}
                  tick={<SalesPeriodTick series={salesSeries} />}
                  tickLine={false}
                  axisLine={{ stroke: colors.border.light }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: colors.text.muted }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                  allowDecimals={false}
                />
                <RechartsTooltip
                  cursor={{ fill: colors.border.light, opacity: 0.4 }}
                  labelFormatter={(value) => {
                    const point = salesSeries.find(p => p.period === value);
                    if (!point) return '';
                    return point.partial ? `${point.label} (${t('machines_salesPartialPeriod')})` : point.label;
                  }}
                  formatter={(value) => [value, t('units')]}
                  contentStyle={{
                    fontSize: '11px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border.light}`
                  }}
                />
                <Bar dataKey="units" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  {salesSeries.map(point => (
                    <Cell key={point.period} fill={colors.primary.main} fillOpacity={point.partial ? 0.4 : 1} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          {!salesLoading && salesSeries.length === 0 && (
            <Typography sx={{ fontSize: '12px', color: colors.text.muted, textAlign: 'center', py: 2 }}>
              {t('noDataAvailable')}
            </Typography>
          )}
        </Box>

        <Divider />

        <RecentSalesSection
          storeId={open ? storeId : null}
          productId={productId}
          clientId={clientId}
          slots={machineData?.slots}
          currentSlot={slot}
          onJumpToSlot={onJumpToSlot}
        />

      </Box>
    </Drawer>
  );
};

export default React.memo(SlotDetailDrawer);
